import cn from "@/v2-ui/utils/utils.cn"
import Accordion from "@/v2-ui/accordion"
import type { PublicCategoryStatus } from "@/v2-status-client/status/status.types"
import StatusSummaryRow from "@/v2-status-client/status/summary/StatusSummaryRow"
import StatusCategoryServiceList from "@/v2-status-client/status/category/StatusCategoryServiceList"
import StatusCategorySubGroup from "@/v2-status-client/status/category/StatusCategorySubGroup"

export type StatusCategoryAccordionItemProps = {
  category: PublicCategoryStatus
}

// One collapsible category item for the per-platform detail view.
// Trigger reuses the shared summary row so the row layout (dot,
// name, description, summary, history strip, uptime%) lines up
// with the platform aggregate row above and with provider rows
// elsewhere on the page. The accordion content is the flat list of
// services that belong to this category, or — when the parent
// carries sub-categories (Map Layers / v2-console mirror) — the
// sub-categories rendered as labelled groupings.
//
// The id on the outer Item is what the URL hash deep links to
// (`#category-foo`); the scroll-mt-* keeps the row clear of the
// sticky platform pills when jumped to.
//
// Trigger is `position: sticky` so the heading stays visible while
// the customer scrolls through the category's services. Together
// with the sticky platform pill row above it, this gives the
// reader two anchor points (which platform they're looking at,
// which category they're inside) without needing to scroll back to
// the top.
//
// Note on `!`-prefixed Tailwind: the v2-ui accordion ships its own
// trigger styling (vertical margin, hover-underline, gap, etc.)
// that's tuned for the in-app shell. We override with `!important`
// utilities because tailwind-merge can't dedupe a custom
// `ui-accordion-trigger` against bare utilities -- the hand-rolled
// class wins by specificity otherwise. Keeping the override list
// short and grouped here so the next reader can see exactly which
// rules we're undoing.
function StatusCategoryAccordionItem(
  props: Readonly<StatusCategoryAccordionItemProps>,
) {
  const { category } = props
  const subCategories = category.subCategories ?? []
  const hasServices = category.services.length > 0
  const hasSubCategories = subCategories.length > 0
  return (
    <Accordion.Item
      value={category.id}
      id={`category-${category.id}`}
      className={cn(
        "rounded-lg border border-border/60 bg-background/30",
        "scroll-mt-20",
        // The shared accordion item adds a bottom border for in-app
        // stacks; here every item is a self-contained card so we
        // remove it and rely on the parent gap for separation.
        "!border-b",
      )}
    >
      <Accordion.Trigger
        aria-label={`Toggle ${category.name} services`}
        className={cn(
          "!items-center w-full text-left",
          // Sticky so the heading travels with the scroll while the
          // customer is reading down a long service list. `top-16`
          // sits just below the platform pill row; `z-20` keeps it
          // above the sub-category headings inside the panel and
          // below the sticky platform pills (z-30).
          "sticky top-16 z-20 bg-background/95 backdrop-blur",
          "rounded-t-lg",
          "!my-0 px-4 py-1 sm:px-5",
          "hover:!no-underline hover:bg-muted/30 focus-visible:bg-muted/30",
          "data-[state=open]:bg-muted/40 data-[state=open]:border-b data-[state=open]:border-border/60 data-[state=open]:rounded-b-none",
          "transition-colors",
        )}
      >
        <StatusSummaryRow
          as="div"
          name={category.name}
          description={category.description}
          status={category.status}
          summary={category.summary}
          uptimePercent30d={category.uptimePercent30d ?? null}
          className="border-none py-3"
        />
      </Accordion.Trigger>
      <Accordion.Content
        className={cn(
          "bg-muted/10 px-3 py-2 sm:px-4",
          "!pb-3 !pt-2",
          "rounded-b-lg",
        )}
      >
        {hasSubCategories ? (
          // Sub-groups stack flat; each carries its own top-border
          // (except the first) so adjacent groups read as distinct
          // sections without needing a wrapping card per group.
          <div className="flex flex-col">
            {subCategories.map((sub) => (
              <StatusCategorySubGroup key={sub.id} category={sub} />
            ))}
          </div>
        ) : hasServices ? (
          <StatusCategoryServiceList services={category.services} />
        ) : (
          <p className="m-0 text-xs text-muted-foreground">
            No individual services to show.
          </p>
        )}
      </Accordion.Content>
    </Accordion.Item>
  )
}

export default StatusCategoryAccordionItem
